import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useNavigate } from "react-router-dom";
import BuildPageStore from "~/stores/BuildPageStore";
import Job, { type Props as JobComponentProps } from "~/components/job/Job";
import { useCallback, useEffect } from "react";
import { CommandJob } from "~/components/build/Show/lib/types";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import { useQueryClient } from "@tanstack/react-query";
import { LogLineSelectionProvider } from "~/components/build/Show/lib/useLogLineSelection";
import { jobQueryOptions } from "~/components/build/Show/lib/queries/job";
import { replicaLagRetryOptions } from "~/components/build/Show/lib/replicaLagRetry";
import { testFailuresQueryOptions } from "~/components/build/Show/lib/queries/testFailures";
import { shouldFetchTestFailureMarkers } from "~/components/job/Job/Log/useTestFailureMarkers";
import { useJobTabSync } from "~/components/build/Show/components/StepDrawer/useJobTabSync";
import { useStepDrawer } from "~/components/build/Show/components/StepDrawer/StepDrawerContext";

export const CommandJobDetails = () => {
  const { job } = useStepDrawer();
  const { build, setBuildData } = useBuild();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Sync tab between URL and store, get active tab and change handler.
  // Always call the hook (Rules of Hooks), but disable when the flag is off.
  const tabSync = useJobTabSync({ job, enabled: Features.BuildJobTabUrls });
  const { activeTab, onTabChange } = Features.BuildJobTabUrls
    ? tabSync
    : { activeTab: undefined, onTabChange: undefined };

  useEffect(() => {
    if (job) {
      BuildPageStore.expandJob(job);
    }
  }, [job?.id]);

  // Prefetch test failure markers as soon as the job drawer opens
  const commandJob = job && job.type === "script" ? (job as CommandJob) : null;
  useEffect(() => {
    if (commandJob && shouldFetchTestFailureMarkers(commandJob)) {
      queryClient.prefetchQuery(testFailuresQueryOptions(build, commandJob.id));
    }
  }, [commandJob?.id, build]);

  const handleRedirectToLatestJob = useCallback(
    (latestJobUUID: string) => {
      if (latestJobUUID) {
        if (Features.ProgressiveBuildShow) {
          // Fetch the job data before navigating to prevent a flash of error state.
          // The new job may be temporarily unavailable due to replica lag.
          queryClient
            .fetchQuery(jobQueryOptions(build, latestJobUUID, replicaLagRetryOptions))
            .then(() => {
              navigate(resolveJobUrl(latestJobUUID));
            })
            .catch(() => {
              // If the job still can't be fetched after retries, navigate anyway
              // so the drawer can show the error state rather than silently failing.
              navigate(resolveJobUrl(latestJobUUID));
            });
        } else {
          navigate(resolveJobUrl(latestJobUUID));
        }
      }
    },
    [build, queryClient, navigate],
  );

  if (!job) {
    return null;
  }

  return (
    <LogLineSelectionProvider>
      <div className="job-list-pipeline flex h-full flex-col">
        <Job
          key={job.id}
          job={job as JobComponentProps["job"]}
          build={build}
          setBuildData={setBuildData}
          autoFollow={true}
          onRetrySuccess={handleRedirectToLatestJob}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
      </div>
    </LogLineSelectionProvider>
  );
};
